import { ref, type Ref } from "vue";
import { CompanyRepository } from "~/repositories/CompanyRepository";
import type { CompanyDto } from "~/types/company";

// Global cache for Company data (client-only)
const companyCache = new Map<number | string, CompanyItem>();

// Company data type with coverImages and companyTags properties
export type CompanyItem = CompanyDto & {
	coverImages: string[];
	companyTags: string[];
};

/**
 * The useCompany composable fetches data for a specific company from the API.
 *
 * @example
 * const { companyItem, loading, error } = useCompany(companyIdOrSlug);
 * console.log(companyItem.value); // Company data
 * console.log(companyItem.value.coverImages); // Company cover images (Cloudinary URLs)
 *
 */
export function useCompany(companyIdOrSlug: number | string) {
	const companyRepository = new CompanyRepository();
	const companyItem: Ref<CompanyItem | null> = ref(null);
	const loading = ref(true);
	const error = ref<Error | null>(null);

	// Main Company fetch logic
	const fetchCompany = async (idOrSlug: number | string) => {
		loading.value = true;
		error.value = null;

		// Cache check (client-only to avoid SSR memory growth)
		if (process.client) {
			const cached = companyCache.get(idOrSlug);
			if (cached) {
				companyItem.value = cached;
				loading.value = false;
				return;
			}
		}

		try {
			const data: CompanyDto | null = await companyRepository.getCompany(idOrSlug);

			if (data) {
				// Load cover images
				let coverImages: string[] = [];
				if (data.boxes?.team_photo?.data) {
					coverImages = data.boxes.team_photo.data.map((img) => {
						return useCloudinary().url(img.cloudinary + ".jpg");
					});
				}

				// Collect company tags
				const companyTags: string[] = [];
				const neededTags = ["industry", "location", "company_size"];
				neededTags.forEach((tag) => {
					if (data.tags?.[tag] != undefined) {
						if (tag == "location") {
							companyTags.push(data.tags[tag].data[0].name);
						} else {
							const tagItem = data.tags[tag];
							if ("name" in tagItem) {
								companyTags.push(tagItem.name);
							}
						}
					}
				});

				// Assemble the final object
				const finalCompanyItem: CompanyItem = {
					...data,
					coverImages,
					companyTags,
				};

				companyItem.value = finalCompanyItem;
				if (process.client) {
					companyCache.set(idOrSlug, finalCompanyItem); // Caching (client-only)
				}
			} else {
				error.value = new Error("A Company not found.");
				companyItem.value = null;
			}
		} catch (err) {
			error.value = err as Error;
			companyItem.value = null;
		} finally {
			loading.value = false;
		}
	};

	fetchCompany(companyIdOrSlug);

	return { companyItem, loading, error };
}
